import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/arbiter/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  annualizeTrialRevenue,
  shouldShowTrialRevenueLift,
} from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import StarsIcon from "@redotech/redo-icons/stars-01.svg";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { memo, useState } from "react";
import { RescuePanelAttribution, useRescuePanelAttribution } from "../util";

const NUMBER_FORMAT = new Intl.NumberFormat();

interface EarnedStat {
  /** The numeric figure, rendered bold and intentionally left small. */
  value: string;
  /** The surrounding sentence, rendered larger and muted. */
  label: string;
}

/**
 * Suite-wide lock page once a self-serve intent free trial has lapsed without
 * Shopify billing (isIntentTrialExpiredUnpaid). Shown on Dashboard, Spotlights,
 * and every ConversionFeatureGate route. The same predicate stops storefront
 * rescue-panel serving. Accepting billing flips the predicate and restores the
 * suite with no further action.
 *
 * Deliberately a single centered call-to-action rather than a dashboard:
 * when the trial cleared the annualized revenue floor, its results are
 * summarized as a few short sentences (bold figure + larger muted copy) to
 * anchor the reactivation ask in concrete value. Fetches its own attribution
 * so it can be mounted from any page without the caller plumbing dashboard
 * state through.
 */
export const IntentTrialExpired = memo(function IntentTrialExpired() {
  const conversionRpcClient = useRequiredContext(
    RedoConversionRpcClientContext,
  );
  const attribution = useRescuePanelAttribution();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same flow as the old wizard billing step: enableIntentRescueBilling
  // mints Redo's umbrella Shopify usage subscription and hands back the
  // confirmation URL. After the merchant approves, the confirm-subscription
  // webhook records the active subscription, isConversionBillingAccepted
  // flips true, and both this lock page and the storefront gate clear on
  // the next load.
  const handleAcceptBilling = useHandler(async () => {
    if (pending) {
      return;
    }
    setError(null);
    setPending(true);
    try {
      const { confirmationUrl } =
        await conversionRpcClient.enableIntentRescueBilling({});
      window.location.href = confirmationUrl;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not start the Shopify billing flow. Please try again.",
      );
      setPending(false);
    }
  });

  const earnedStats = buildEarnedStats(attribution);

  return (
    <Flex
      align="center"
      dir="column"
      justify="center"
      minH="three-quarter-screen"
      px="3xl"
      py="7xl"
      w="full"
    >
      <Flex align="center" dir="column" gap="4xl" maxW="sm" w="full">
        <Flex align="center" dir="column" gap="2xl">
          <Flex align="center" dir="column" gap="lg">
            <RedoFeaturedIcon color="brand" Icon={StarsIcon} size="lg" />
            <Text fontSize="6xl" fontWeight="bold" textAlign="center">
              Your free trial is over!
            </Text>
          </Flex>

          {earnedStats.length > 0 && (
            <Flex align="center" dir="column" gap="md">
              <Text fontSize="2xl" textColor="secondary">
                So far the rescue Popup has earned you:
              </Text>
              <Flex align="flex-start" dir="column" gap="md" maxW="xs" py="xl">
                {earnedStats.map((stat) => (
                  <Flex align="baseline" gap="md" key={stat.label}>
                    <Text fontSize="lg" textColor="secondary">
                      &bull;
                    </Text>
                    <Text as="p" fontSize="lg" textColor="secondary">
                      <Text
                        as="span"
                        fontSize="md"
                        fontWeight="bold"
                        textColor="primary"
                      >
                        {stat.value}
                      </Text>{" "}
                      {stat.label}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        <Flex align="center" dir="column" gap="xl">
          <Text fontSize="lg" textAlign="center" textColor="secondary">
            Enable billing to keep your Popups running.
          </Text>
          <RedoButton
            dataTestId="intent-trial-expired-enable-billing"
            dataTrackId="conversion.intent-trial-expired.enable-billing"
            hierarchy="primary"
            onClick={() => {
              void handleAcceptBilling();
            }}
            pending={pending}
            size="lg"
            text="Enable billing in Shopify"
          />
          {error && (
            <Text fontSize="sm" textAlign="center" textColor="error">
              {error}
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

/**
 * Short stats summarizing what the trial produced, each split into a bold
 * value and a larger muted label.
 *
 * The annualized recovered revenue is the gate for the entire section: unless
 * it clears the showcase floor (> $1200/yr run-rate) we return no stats at
 * all, so a weak or empty trial shows only the reactivation ask rather than
 * underwhelming numbers. The revenue line leads with the raw trial total and
 * carries the annualized projection in parentheses.
 */
function buildEarnedStats(
  attribution: RescuePanelAttribution | null,
): EarnedStat[] {
  if (attribution === null) {
    return [];
  }

  const annualizedRevenue = annualizeTrialRevenue(
    attribution.revenueFromPanel.amount,
  );
  if (!shouldShowTrialRevenueLift(annualizedRevenue)) {
    return [];
  }

  const currencyFormatter = getCurrencyFormatter(
    attribution.revenueFromPanel.currency,
  );
  const conversionRateLift =
    attribution.conversionRateChangeComparedToPreviousPeriod ?? 0;

  return [
    {
      value: currencyFormatter.format(attribution.revenueFromPanel.amount),
      label: `total revenue recovered (${currencyFormatter.format(
        annualizedRevenue,
      )} annualized)`,
    },
    {
      value: `${Number(conversionRateLift.toFixed(1))}%`,
      label: "lift in conversion rate",
    },
    {
      value: NUMBER_FORMAT.format(attribution.numImpressions),
      label: "rescue Popup impressions",
    },
  ];
}
